import { useEffect, useMemo } from "react";
import { Box, Button, Card, CardContent, CircularProgress, Container, IconButton, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { formatCurrency, type SupportedCurrency } from "@utils/currencyUtils";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import { useToken } from "@contexts/TokenContext";
import { useCartInfo } from "@contexts/CartInfoContext";
import { getLicenseSkusFromProducts, AVAILABLE_LICENSE_SKUS } from "@utils/licenseSkuUtils";
import type { OrganizationProducts } from "@api/private/getOrganization";

export default function Licenses() {
  const { watch, setValue } = useFormContext();
  const navigate = useNavigate();
  const { isAuthenticated } = useToken();
  const selectedCurrency = (watch("selectedCurrency") as SupportedCurrency) || "usd";
  const items = watch("items") || [];
  const organizationProducts = (watch("organizationProducts") as OrganizationProducts) || {};

  // Derive available license SKUs from organization products
  // Non-authenticated users see all license types
  const availableLicenses = useMemo(() => {
    if (!isAuthenticated) {
      return [...AVAILABLE_LICENSE_SKUS];
    }
    return getLicenseSkusFromProducts(organizationProducts);
  }, [isAuthenticated, organizationProducts]);

  // Set SKUs to fetch in form state - triggers lazy fetch in CartInfoContext
  useEffect(() => {
    if (availableLicenses.length > 0) {
      setValue("licenseSKUsToFetch", availableLicenses);
    }
  }, [availableLicenses, setValue]);

  // Get license products from CartInfoContext (single source of truth)
  const { licenseProducts, isLoadingLicenseProducts } = useCartInfo();

  const updateQuantity = (sku: string, delta: number) => {
    const existingItem = items.find((item: { sku: string; quantity: number }) => item.sku === sku);
    const currentQty = existingItem ? existingItem.quantity : 0;
    const newQty = Math.max(0, currentQty + delta);

    if (newQty === 0) {
      // Remove item from cart
      const filtered = items.filter((item: { sku: string }) => item.sku !== sku);
      setValue("items", filtered);
    } else if (existingItem) {
      // Update existing item
      const updatedItems = items.map((item: { sku: string; quantity: number }) =>
        item.sku === sku ? { ...item, quantity: newQty } : item
      );
      setValue("items", updatedItems);
    } else {
      // Add new item
      const newItem = { sku, quantity: newQty };
      setValue("items", [...items, newItem]);
    }
  };

  const getLicenseQuantity = (sku: string) => {
    const item = items.find((item: { sku: string; quantity: number }) => item.sku === sku);
    return item ? item.quantity : 0;
  };

  const handleCheckout = () => {
    // Reset to step 1 and navigate to home
    setValue("currentStep", 1);
    navigate("/");
  };

  const totalLicensesInCart = items
    .filter((item: { sku: string }) => licenseProducts.some((lp) => lp.sku === item.sku))
    .reduce((sum: number, item: { quantity: number }) => sum + item.quantity, 0);

  const isLoading = isLoadingLicenseProducts;

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          Purchase Client Licenses
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Add additional client licenses to your organization. Each license allows one more client to
          access your subscriptions.
        </Typography>
      </Box>

      {availableLicenses.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              No Licenses Available
            </Typography>
            <Typography color="text.secondary">
              You need an active subscription with completed certification to purchase client licenses.
              Please contact support if you believe this is an error.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {availableLicenses.map((sku) => {
            const product = licenseProducts.find((p) => p.sku === sku);
            if (!product) return null;

            const quantity = getLicenseQuantity(sku);

            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={sku}>
                <Card
                  variant="outlined"
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {product.title}
                    </Typography>
                    <Typography color="text.secondary" gutterBottom>
                      {product.description}
                    </Typography>
                    <Typography variant="h5" color="primary" gutterBottom>
                      {formatCurrency(product.price, selectedCurrency)}
                    </Typography>
                  </CardContent>
                  <Box p={2} pt={0}>
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
                      <IconButton
                        onClick={() => updateQuantity(sku, -1)}
                        disabled={quantity === 0}
                        color="primary"
                        size="small"
                      >
                        <RemoveIcon />
                      </IconButton>
                      <Typography variant="h6" sx={{ minWidth: "40px", textAlign: "center" }}>
                        {quantity}
                      </Typography>
                      <IconButton
                        onClick={() => updateQuantity(sku, 1)}
                        color="primary"
                        size="small"
                      >
                        <AddIcon />
                      </IconButton>
                    </Stack>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {totalLicensesInCart > 0 && (
        <Box mt={4}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">
                  {totalLicensesInCart} license{totalLicensesInCart !== 1 ? "s" : ""} in cart
                </Typography>
                <Button variant="contained" size="large" onClick={handleCheckout}>
                  Proceed to Checkout
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}
    </Container>
  );
}
